// frontend/src/context/SAMLContext.jsx

import React, { createContext, useContext, useEffect, useState } from 'react';
import samlApi from '../api/samlApi.js';

const SAMLContext = createContext();

export const useSAML = () => {
  const context = useContext(SAMLContext);
  if (!context) {
    throw new Error('useSAML must be used within a SAMLProvider');
  }
  return context;
};

export function SAMLProvider({ children }) {
  const [samlConfig, setSamlConfig] = useState(null);
  const [identityProviders, setIdentityProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load SAML configuration
   */
  const loadConfig = async () => {
    try {
      setLoading(true);
      const config = await samlApi.getConfig();
      setSamlConfig(config);
      setIdentityProviders(config.identityProviders || []);
      setInitialized(config.initialized);
      setError(null);
    } catch (err) {
      // Only log SAML config errors in production or when specifically needed
      if (import.meta.env.PROD || import.meta.env.VITE_DEBUG_SAML) {
        console.error('Failed to load SAML config:', err);
      }
      setError(err.message);
      // Don't throw error - SAML might not be configured yet
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initialize SAML provider
   */
  const initializeSAML = async (config = {}) => {
    try {
      setLoading(true);
      const result = await samlApi.initialize(config);
      await loadConfig(); // Reload config after initialization
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add Identity Provider
   */
  const addIdentityProvider = async idpConfig => {
    try {
      const result = await samlApi.addIdentityProvider(idpConfig);
      await loadConfig(); // Reload config after adding IdP
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Test SAML configuration
   */
  const testConfiguration = async idpName => {
    try {
      const result = await samlApi.testConfiguration(idpName);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Initiate SAML login
   */
  const initiateLogin = (idpName, returnUrl = null) => {
    try {
      samlApi.initiateLogin(idpName, returnUrl);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Initiate SAML logout
   */
  const initiateLogout = idpName => {
    try {
      samlApi.initiateLogout(idpName);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Download Service Provider metadata
   */
  const downloadMetadata = async () => {
    try {
      await samlApi.downloadServiceProviderMetadata();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Get IdP templates
   */
  const getIdpTemplates = () => {
    return samlApi.getIdpTemplates();
  };

  /**
   * Validate certificate
   */
  const validateCertificate = async certificate => {
    return samlApi.validateCertificate(certificate);
  };

  /**
   * Handle SAML authentication result
   */
  const handleAuthenticationResult = () => {
    const result = samlApi.parseAuthenticationResult();
    if (result) {
      samlApi.clearAuthenticationResult();
      return result;
    }
    return null;
  };

  /**
   * Check if SAML is available
   */
  const isSAMLAvailable = () => {
    return initialized && identityProviders.length > 0;
  };

  /**
   * Get available identity providers
   */
  const getAvailableIdps = () => {
    return identityProviders.filter(idp => idp.enabled);
  };

  /**
   * Check if IdP is configured
   */
  const isIdpConfigured = idpName => {
    return identityProviders.some(idp => idp.name === idpName && idp.enabled);
  };

  // Load configuration on mount
  useEffect(() => {
    loadConfig();
  }, []);

  // Handle SAML authentication result on mount
  useEffect(() => {
    const authResult = handleAuthenticationResult();
    if (authResult) {
      if (authResult.success) {
        // Store token and trigger authentication
        localStorage.setItem('token', authResult.token);
        localStorage.setItem('authMethod', 'saml');

        // Notify parent components about successful authentication
        window.dispatchEvent(
          new CustomEvent('saml-auth-success', {
            detail: authResult,
          })
        );
      } else {
        // Handle authentication error
        setError(authResult.error);
        window.dispatchEvent(
          new CustomEvent('saml-auth-error', {
            detail: authResult,
          })
        );
      }
    }
  }, []);

  const value = {
    // State
    samlConfig,
    identityProviders,
    loading,
    initialized,
    error,

    // Actions
    loadConfig,
    initializeSAML,
    addIdentityProvider,
    testConfiguration,
    initiateLogin,
    initiateLogout,
    downloadMetadata,
    getIdpTemplates,
    validateCertificate,
    handleAuthenticationResult,

    // Computed values
    isSAMLAvailable,
    getAvailableIdps,
    isIdpConfigured,

    // Clear error
    clearError: () => setError(null),
  };

  return <SAMLContext.Provider value={value}>{children}</SAMLContext.Provider>;
}
